import { Box } from "@mui/material";
import { Grid2 as Grid } from "@mui/material";
import { Button } from "../../../../common";
import { useValidateForm } from "../../../../../hooks";
import { SelectBox } from "../../../../common";
import { DropdownItem, PaymentReminder } from "../../../../../types";
import { editOrganizationPaymentReminderSchema } from "../../../../../validations";
import { useState } from "react";
import {
  isCreateOrganizationModeAtom,
  selectedOrgIdAtom,
} from "../../../../../atoms";
import { useAtom } from "jotai";

const enabledOptions: DropdownItem[] = [
  { key: "Enabled", value: 1 },
  { key: "Disabled", value: 0 },
];

const daysOptions: DropdownItem[] = Array.from({ length: 30 }, (_, i) => ({
  key: String(i + 1),
  value: i + 1,
}));

type EditOrganizationProfileBodyProps = {
  defaultValues?: PaymentReminder;
  handleEdit: (data: PaymentReminder) => void;
  handleDiscard: () => void;
};

export const EditOrganizationPaymentReminder = ({
  defaultValues,
  handleEdit,
  handleDiscard,
}: EditOrganizationProfileBodyProps) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [isCreateOrganizationMode] = useAtom(isCreateOrganizationModeAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const isOrgNotYetCreated =
    isCreateOrganizationMode && (!selectedOrgId || selectedOrgId === 0);

  const { register, handleSubmit, errors } = useValidateForm(
    editOrganizationPaymentReminderSchema,
  );

  const handleChange = () => {
    setHasChanges(true);
  };

  const onSubmit = (data: {
    paymentReminder: string | number;
    frequencyDays: number;
  }) => {
    const mappedData: PaymentReminder = {
      enabled: Boolean(data.paymentReminder),
      frequencyDays: data.frequencyDays,
    };
    handleEdit(mappedData);
  };

  return (
    <Box
      onSubmit={handleSubmit(onSubmit)}
      component={"form"}
      noValidate
      sx={{
        height: "100%",
      }}
    >
      <Grid
        container
        spacing={2}
        direction={{ lg: "row", xs: "column" }}
        sx={{
          width: "100%",
          height: "100%",
          px: 3,
          py: 3,
          alignItems: "center",
        }}
      >
        <Grid
          container
          size={{ lg: 5, xs: 12 }}
          gap={3}
          sx={{
            px: { md: 4, xs: 0, sm: 2 },
            py: 0,
            flexDirection: "column",
          }}
        >
          <Grid container size={12}>
            <SelectBox
              register={register}
              defaultValue={Number(defaultValues?.enabled)}
              label="Payment Reminder"
              name="paymentReminder"
              dropdown={enabledOptions}
              error={errors["paymentReminder"]}
              onChange={handleChange}
              disabled={isOrgNotYetCreated}
            />
          </Grid>
        </Grid>
        <Grid
          container
          size={{ lg: 5, xs: 12 }}
          gap={3}
          sx={{
            px: { md: 4, xs: 0, sm: 2 },
            py: 1.6,
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <Grid container size={12}>
            <SelectBox
              register={register}
              defaultValue={defaultValues?.frequencyDays}
              label="Frequency (Days)"
              name="frequencyDays"
              dropdown={daysOptions}
              error={errors["frequencyDays"]}
              onChange={handleChange}
              valueAsNumber={true}
              disabled={isOrgNotYetCreated}
            />
          </Grid>
        </Grid>
        <Grid
          container
          size={{ xs: 12, lg: 2 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            pl: { md: 4, xs: 0, sm: 2, lg: 0 },
            pr: { md: 4, xs: 0, sm: 2, lg: 2 },
            pt: { lg: 0, xs: 2 },
            pb: 0,
            height: { lg: "100%", xs: "fit-content" },
          }}
        >
          {!isCreateOrganizationMode && (
            <Button
              label="Discard"
              variantType={"secondary"}
              sx={{
                padding: "0.5rem",
                width: "100%",
              }}
              onClick={handleDiscard}
            />
          )}
          <Button
            type={hasChanges ? "submit" : undefined}
            disabled={!hasChanges || isOrgNotYetCreated}
            label="Save Changes"
            variantType={
              hasChanges && !isOrgNotYetCreated ? "primary" : "secondary"
            }
            sx={{
              padding: "0.5rem",
              width: "100%",
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
